import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Search } from "lucide-react";
import { STATUS_META, POLICY_STATUS_META } from "@/lib/api";

export const LIST_FILTER_KEYS = ["merchant", "employee", "department", "category", "policy", "policy_status", "status", "gl"];

export const FILTER_LABELS = {
  merchant: "Merchant", employee: "Employee", department: "Department", category: "Category",
  policy: "Policy", policy_status: "In Policy", status: "Status", gl: "GL", date: "Date", amount: "Amount",
};

export const filterOptionsFor = (key, options) => {
  if (!options) return [];
  switch (key) {
    case "merchant": return (options.merchants || []).map((m) => ({ value: m, label: m }));
    case "employee": return (options.employees || []).map((e) => ({ value: e.id, label: e.name }));
    case "department": return (options.departments || []).map((d) => ({ value: d, label: d }));
    case "category": return (options.categories || []).map((c) => ({ value: c, label: c }));
    case "policy": return (options.policies || []).map((p) => ({ value: p.id, label: p.name }));
    case "policy_status": return (options.policy_statuses || []).map((s) => ({ value: s, label: POLICY_STATUS_META[s]?.label || s }));
    case "status": return (options.statuses || []).map((s) => ({ value: s, label: STATUS_META[s]?.label || s }));
    case "gl": return (options.gls || []).map((g) => ({ value: g, label: g }));
    default: return [];
  }
};

export const valueLabel = (key, value, options) => {
  const opt = filterOptionsFor(key, options).find((o) => o.value === value);
  return opt ? opt.label : value;
};

export const FilterValueList = ({ options, selected, onToggle, testPrefix = "filter" }) => {
  const [q, setQ] = useState("");
  const filtered = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      {options.length > 7 && (
        <div className="relative mb-1.5">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#555]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            data-testid={`${testPrefix}-search`}
            className="h-7 border-[#2a2a2a] bg-[#111] pl-7 text-[12px] text-white placeholder:text-[#555]" />
        </div>
      )}
      <div className="max-h-52 overflow-y-auto">
        {filtered.map((o) => (
          <label key={o.value}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[#1c1c1c]">
            <Checkbox checked={selected.includes(o.value)} onCheckedChange={() => onToggle(o.value)}
              data-testid={`${testPrefix}-option-${String(o.value).replace(/\s/g, "-")}`}
              className="border-[#333] data-[state=checked]:bg-[#4F8EF7] data-[state=checked]:border-[#4F8EF7]" />
            <span className="truncate text-[12px] text-[#e5e5e5]">{o.label}</span>
          </label>
        ))}
        {filtered.length === 0 && <div className="px-2 py-3 text-center text-[11px] text-[#555]">No matches</div>}
      </div>
    </div>
  );
};

export const DateRangeControl = ({ from, to, onChange, testPrefix = "filter" }) => (
  <div className="space-y-2">
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#6b6b6b]">From</div>
      <Input type="date" value={from || ""} onChange={(e) => onChange(e.target.value, to)}
        data-testid={`${testPrefix}-date-from`}
        className="h-8 border-[#2a2a2a] bg-[#111] text-[12px] tabular-nums text-white [color-scheme:dark]" />
    </div>
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#6b6b6b]">To</div>
      <Input type="date" value={to || ""} onChange={(e) => onChange(from, e.target.value)}
        data-testid={`${testPrefix}-date-to`}
        className="h-8 border-[#2a2a2a] bg-[#111] text-[12px] tabular-nums text-white [color-scheme:dark]" />
    </div>
  </div>
);

export const AmountRangeControl = ({ min, max, onApply, testPrefix = "filter" }) => {
  const [lo, setLo] = useState(min ?? "");
  const [hi, setHi] = useState(max ?? "");
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Min $</div>
          <Input type="number" min="0" value={lo} onChange={(e) => setLo(e.target.value)} placeholder="0"
            data-testid={`${testPrefix}-amount-min`}
            className="h-8 border-[#2a2a2a] bg-[#111] text-[12px] tabular-nums text-white placeholder:text-[#555]" />
        </div>
        <div className="flex-1">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Max $</div>
          <Input type="number" min="0" value={hi} onChange={(e) => setHi(e.target.value)} placeholder="∞"
            data-testid={`${testPrefix}-amount-max`}
            className="h-8 border-[#2a2a2a] bg-[#111] text-[12px] tabular-nums text-white placeholder:text-[#555]" />
        </div>
      </div>
      <button onClick={() => onApply(lo, hi)} data-testid={`${testPrefix}-amount-apply`}
        className="w-full rounded-lg bg-[rgba(79,142,247,0.15)] py-1.5 text-[11.5px] font-medium text-[#7FAEF9] transition-colors hover:bg-[rgba(79,142,247,0.28)]">
        Apply range
      </button>
    </div>
  );
};
